"use client";

/**
 * WebVitalsReporter.tsx — client-side CWV + JS error RUM mount point.
 *
 * Renders null. Side-effects only:
 *   - Dynamically imports web-vitals/attribution (async chunk, never SSR).
 *   - Subscribes to INP, LCP, CLS, TTFB via the attribution API.
 *   - Registers window error + unhandledrejection listeners (PII-stripped).
 *   - Registers flush listeners (visibilitychange + pagehide).
 *
 * PII Controls:
 *   - route_template only (via toRouteTemplate); raw pathname NEVER emitted.
 *   - error messages: truncated to 120 chars, no stack, no query/fragment.
 *   - Error-message scrubber strips known PII patterns (JWT bearer, inviteToken,
 *     api_key, db_uri, query param values) before buffering.
 *
 * Extended scope NOT implemented here: soft-nav duration.
 *
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { registerFlushListeners, reportRumEvent } from "@/lib/rum/reporter";
import { toRouteTemplate } from "@/lib/rum/routeTemplate";

// ---------------------------------------------------------------------------
// PII scrubber for error messages
// ---------------------------------------------------------------------------

/** Patterns that indicate the substring carries a secret / identity value. */
const PII_PATTERNS: readonly RegExp[] = [
  /Bearer\s+\S+/gi, // Authorization: Bearer <token>
  /inviteToken[=:]\s*\S+/gi, // inviteToken=<value>
  /api[_-]?key[=:]\s*\S+/gi, // api_key=<value>
  /db[_-]?uri[=:]\s*\S+/gi, // db_uri=<value>
  /\bquery[=:]\s*\S+/gi, // query=<value>
  /[?&][\w-]+=[^&\s]{8,}/g, // generic query param values >= 8 chars
];

const MAX_MESSAGE_LENGTH = 120;

function scrubErrorMessage(raw: string): string {
  let msg = raw;
  for (const pattern of PII_PATTERNS) {
    msg = msg.replace(pattern, "[redacted]");
  }
  // Truncate after scrub (redacted placeholders may expand).
  return msg.slice(0, MAX_MESSAGE_LENGTH);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WebVitalsReporter(): null {
  const pathname = usePathname();

  // The current route template, read by the mount-once subscriptions below.
  // web-vitals callbacks fire across the page lifetime (e.g. INP/CLS finalize at
  // page-hide), so the reported template must be whatever was active then —
  // tracked via a ref so subscriptions register ONCE, not per soft-nav.
  const routeRef = useRef<string>(toRouteTemplate(pathname ?? "/"));
  routeRef.current = toRouteTemplate(pathname ?? "/");

  useEffect(() => {
    // Register sendBeacon / pagehide flush once.
    registerFlushListeners();

    // Subscribe to CWV ONCE per page load. Re-subscribing on every soft-nav
    // would register duplicate callbacks → duplicate reports. The route is read
    // from routeRef at report time.
    void import("web-vitals/attribution").then(
      ({ onINP, onLCP, onCLS, onTTFB }) => {
        const report = ({
          name,
          value,
          rating,
        }: {
          name: string;
          value: number;
          rating: string;
        }): void => {
          reportRumEvent({
            type: "cwv",
            name,
            value,
            rating,
            route_template: routeRef.current,
          });
        };
        onINP(report);
        onLCP(report);
        onCLS(report);
        onTTFB(report);
      },
    );

    // JS error listener — PII-stripped, no stack.
    const onError = (ev: ErrorEvent): void => {
      reportRumEvent({
        type: "js_error",
        error_type: "uncaught",
        name: scrubErrorMessage(ev.message ?? "unknown error"),
        route_template: routeRef.current,
      });
    };

    // Unhandled promise rejection listener — PII-stripped, no stack.
    const onUnhandledRejection = (ev: PromiseRejectionEvent): void => {
      const raw =
        ev.reason instanceof Error
          ? ev.reason.message
          : typeof ev.reason === "string"
            ? ev.reason
            : "unhandled_rejection";
      reportRumEvent({
        type: "js_error",
        error_type: "unhandled_rejection",
        name: scrubErrorMessage(raw),
        route_template: routeRef.current,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
    // Mount-once: subscriptions read the live route via routeRef (stable deps).
  }, []);

  return null;
}
